/**
 * About — public company page.
 *
 * The two-sided platform IS the hero: a full-bleed, edge-to-edge split right
 * at the top of the page — hardware in light steel on the left, the API in
 * solid navy on the right — before any other copy. The origin story ("Why we
 * built it") and closing CTA follow beneath it. The deep-dives for each side
 * live on /product (hardware catalog), /hardware (join as a hardware
 * provider), and /developers (software integrations) — About stays
 * high-level and makes no unverifiable claims (founding date, headcount,
 * customer numbers).
 *
 * Nav + footer live in PublicLayout; this page only declares its content.
 */

import { Link } from 'react-router-dom';
import styled from '@emotion/styled';
import { IconArrowRight } from '@tabler/icons-react';
import { PublicLayout } from '../../components/PublicLayout';

const NAVY = '#0d253d';
const STEEL = '#d8e4f0';

// ── Layout primitives ─────────────────────────────────────────────────────────

const Container = styled.div`
  max-width: 1160px;
  margin: 0 auto;
  padding: 0 clamp(20px, 6vw, 64px);
`;

const Section = styled.section`
  padding: clamp(40px, 7vh, 80px) 0;
`;


// ── Story blocks ───────────────────────────────────────────────────────────────

const Block = styled.div`
  max-width: 760px;

  h2 {
    font-size: clamp(24px, 3.4vw, 34px);
    line-height: 1.12;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: ${({ theme }) => theme.font.color.primary};
    margin: 0 0 ${({ theme }) => theme.spacing(5)} 0;
  }
  p {
    font-size: 17px;
    line-height: 1.7;
    color: ${({ theme }) => theme.font.color.secondary};
    margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
  }
  p:last-child { margin-bottom: 0; }
  strong { color: ${({ theme }) => theme.font.color.primary}; font-weight: ${({ theme }) => theme.font.weight.semiBold}; }
`;

// ── Two sides — full-bleed, stark split ────────────────────────────────────────
// Breaks out of Container to the full viewport width so the two halves read as
// one hard division down the page, not a card grid. Left = the hardware, in
// light steel with navy text; right = the API, inverted to solid navy with
// white text — same colour language as the credentials dark card elsewhere on
// the site, just used here as the whole side rather than an accent.

const SplitSection = styled.section`
  width: 100vw;
  margin-left: calc(50% - 50vw);
`;

const SplitGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;

  @media (max-width: 860px) {
    grid-template-columns: 1fr;
  }
`;

const SplitCol = styled.div<{ $dark?: boolean }>`
  background: ${({ $dark }) => ($dark ? NAVY : STEEL)};
  color: ${({ $dark }) => ($dark ? '#fff' : NAVY)};
  padding: clamp(56px, 10vh, 120px) clamp(24px, 6vw, 80px);
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: ${({ $dark }) => ($dark ? 'flex-start' : 'flex-end')};

  .inner { max-width: 440px; text-align: ${({ $dark }) => ($dark ? 'left' : 'right')}; }

  @media (max-width: 860px) {
    align-items: flex-start;
    .inner { text-align: left; }
  }
`;

const SplitEyebrow = styled.p<{ $dark?: boolean }>`
  font-size: 13px;
  font-weight: 700;
  letter-spacing: 0.06em;
  text-transform: uppercase;
  color: ${({ $dark }) => ($dark ? '#9fc1e8' : NAVY)};
  opacity: ${({ $dark }) => ($dark ? 1 : 0.7)};
  margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
`;

const SplitTitle = styled.h3<{ $dark?: boolean }>`
  font-size: clamp(26px, 3.4vw, 38px);
  line-height: 1.1;
  letter-spacing: -0.02em;
  font-weight: 700;
  color: ${({ $dark }) => ($dark ? '#fff' : NAVY)};
  margin: 0 0 ${({ theme }) => theme.spacing(4)} 0;
`;

const SplitText = styled.p<{ $dark?: boolean }>`
  font-size: 16px;
  line-height: 1.65;
  color: ${({ $dark }) => ($dark ? 'rgba(255, 255, 255, 0.75)' : 'rgba(13, 37, 61, 0.75)')};
  margin: 0 0 ${({ theme }) => theme.spacing(6)} 0;
`;

const SplitLink = styled(Link)<{ $dark?: boolean }>`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 48px;
  padding: 0 24px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: ${({ $dark }) => ($dark ? '#fff' : NAVY)};
  color: ${({ $dark }) => ($dark ? NAVY : '#fff')};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 15px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

// ── Closing CTA band ───────────────────────────────────────────────────────────

const CtaBand = styled.div`
  text-align: center;
  padding: clamp(44px, 7vh, 76px) 24px;
  border-radius: ${({ theme }) => theme.border.radius.xl};
  background: linear-gradient(135deg, #1b3a5e 0%, #0d253d 100%);
  color: #fff;

  h2 {
    font-size: clamp(26px, 4vw, 40px);
    line-height: 1.1;
    letter-spacing: -0.02em;
    font-weight: 700;
    color: #fff;
    margin: 0 0 ${({ theme }) => theme.spacing(3)} 0;
  }
  p {
    color: rgba(255, 255, 255, 0.88);
    font-size: 18px;
    line-height: 1.55;
    margin: 0 auto ${({ theme }) => theme.spacing(7)};
    max-width: 52ch;
  }
`;

const CtaActions = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${({ theme }) => theme.spacing(3)};
  justify-content: center;
  align-items: center;
`;

const CtaWhite = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(2)};
  height: 52px;
  padding: 0 26px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  background: #fff;
  color: ${({ theme }) => '#0d253d'};
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: transform 0.15s ease;

  &:hover { transform: translateY(-1px); }
`;

const CtaGhost = styled(Link)`
  display: inline-flex;
  align-items: center;
  gap: ${({ theme }) => theme.spacing(1)};
  height: 52px;
  padding: 0 22px;
  border-radius: ${({ theme }) => theme.border.radius.pill};
  border: 1px solid rgba(255, 255, 255, 0.55);
  color: #fff;
  font-weight: ${({ theme }) => theme.font.weight.semiBold};
  font-size: 16px;
  text-decoration: none;
  transition: background 0.15s ease;

  &:hover { background: rgba(255, 255, 255, 0.12); }
`;

// ── Page ──────────────────────────────────────────────────────────────────────

function About() {
  return (
    <PublicLayout>
      {/* Hero — the stark split itself. Hardware left in light steel,
          API right in solid navy, so the division reads as a hard line down
          the page, not a card grid. */}
      <SplitSection>
        <SplitGrid>
          <SplitCol>
            <div className="inner">
              <SplitEyebrow>The hardware</SplitEyebrow>
              <SplitTitle>Cellular-connected, battery-powered locks.</SplitTitle>
              <SplitText>
                Based in San Diego, we design and build the LTE electronics
                for battery-powered, cellular-connected smart locks —
                deadbolts, levers, mortise, interconnected, and exit trim.
                Every unit ships as a complete, weatherproof lock with its
                own modem and battery. No wiring, no building network.
              </SplitText>
              <SplitLink to="/hardware">
                See the hardware <IconArrowRight size={16} />
              </SplitLink>
            </div>
          </SplitCol>
          <SplitCol $dark>
            <div className="inner">
              <SplitEyebrow $dark>The platform</SplitEyebrow>
              <SplitTitle $dark>One API for every door.</SplitTitle>
              <SplitText $dark>
                Credentials, schedules, live status, and real-time access
                events — every device is reachable through a single API.
                Software providers integrate real hardware into their own
                apps; hardware providers can join the same pipeline so
                software providers can reach them too.
              </SplitText>
              <SplitLink to="/developers" $dark>
                Explore the API <IconArrowRight size={16} />
              </SplitLink>
            </div>
          </SplitCol>
        </SplitGrid>
      </SplitSection>

      {/* Why we built it */}
      <Section>
        <Container>
          <Block>
            <h2>Why we built it</h2>
            <p>
              We saw an issue in the access control market.. It is expensive to install a new door.
              Between the long cable runs, expensive materials, and complicated wiring diagrams
              we thought there had to be an easier way... we built Simkura.
            </p>
            <p>
              We wanted to combine all elements of the access control hardware into one device. No more WiFi routers.
              No more battery backup. Our hardware is ready to use within minutes of installing the lock.
              We hope you find it as easy to use as we do.
            </p>
          </Block>
        </Container>
      </Section>

      {/* Closing CTA */}
      <Section>
        <Container>
          <CtaBand>
            <h2>See both sides of the platform.</h2>
            <p>
              Browse the hardware, explore the API, or find out how to join
              as a hardware provider.
            </p>
            <CtaActions>
              <CtaWhite to="/hardware">
                See the hardware
                <IconArrowRight size={18} />
              </CtaWhite>
              <CtaGhost to="/hardware">Join as a hardware provider</CtaGhost>
            </CtaActions>
          </CtaBand>
        </Container>
      </Section>
    </PublicLayout>
  );
}

export default About;
